import { ApolloQueryResult, useLazyQuery } from "@apollo/client";
import classnames from "classnames";
import { isToday } from "date-fns";
import { useEffect } from "react";
import { FormattedMessage } from "react-intl";
import { useHistory, useLocation } from "react-router";
import { SwiperSlide } from "swiper/react";

import BlockAccordion from "@components/v1/accordions/BlockAccordion";
import styles from "@components/v1/Article.module.css";
import ArticleIntro from "@components/v1/ArticleIntro";
import ArticleIntroMobile from "@components/v1/ArticleIntroMobile";
import DirectionButton from "@components/v1/buttons/DirectionButton";
import ArticleCard from "@components/v1/cards/ArticleCard";
import CardSlides from "@components/v1/layout/CardSlides";
import ExternalLink from "@components/v1/typography/ExternalLink";
import Button from "@components/v2/buttons/Button";
import IconBase from "@components/v2/icons/IconBase";
import Text from "@components/v2/typography/Text";
import useSubscribeToJourney from "@hooks/subscriptions/useSubscribeToJourney";
import useInterval from "@hooks/useInterval";
import useIsMediumOrSmallerScreen from "@hooks/useIsMediumOrSmallerScreen";
import useIsSmallOrSmallerScreen from "@hooks/useIsSmallOrSmallerScreen";
import useMe from "@hooks/useMe";
import useMyClient from "@hooks/useMyClient";
import useSanitizeHtml from "@hooks/useSanitizeHtml";
import useWebinarRegistration from "@hooks/useWebinarRegistration";
import {
  automaticallyRegisterForArticleIdKey,
  automaticallyRegisterForArticleStartTimeKey,
  registerForWebinarAutomaticallyIdParam,
  registerForWebinarAutomaticallyStartTimeParam
} from "@typing/Constants";
import {
  ArticleContentTypeEnum,
  ArticleFragment,
  GetArticleQuery,
  GetJourneyWithExpertDocument,
  GetJourneyWithExpertQuery,
  GetJourneyWithExpertQueryVariables,
  WebinarRegistrationRecurrenceTypeEnum
} from "@typing/Generated";
import { articleHasVideo } from "@utils/articleUtils";

type ArticleProps = {
  article: ArticleFragment;
  journeyId: string | null;
  refetch: () => Promise<ApolloQueryResult<GetArticleQuery>>;
};

const Article = ({ article, journeyId, refetch }: ArticleProps) => {
  const { addInterval, clearIntervals, intervals } = useInterval();
  const myClient = useMyClient();
  const myUser = useMe();
  const isMediumOrSmallerScreen = useIsMediumOrSmallerScreen();
  const isSmallOrSmallerScreen = useIsSmallOrSmallerScreen();
  const webinarIsToday = isToday(article.webinarStartTime);
  const history = useHistory();

  const { createRegistrations } = useWebinarRegistration();

  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const registerAutomaticallyIdParam = params.get(registerForWebinarAutomaticallyIdParam);
  const registerAutomaticallyStartTimeParam = params.get(registerForWebinarAutomaticallyStartTimeParam);

  const [getJourney, getJourneyQuery] = useLazyQuery<GetJourneyWithExpertQuery, GetJourneyWithExpertQueryVariables>(
    GetJourneyWithExpertDocument
  );

  useSubscribeToJourney(journeyId, () => {
    getJourneyQuery.refetch();
  });

  useEffect(() => {
    if (journeyId) {
      getJourney({
        variables: { id: journeyId }
      });
    }
  }, [getJourney, journeyId]);

  useEffect(() => {
    if (article.photoScanning && intervals.current.length === 0) {
      addInterval(() => {
        refetch();
      }, 5000);
    } else if (!article.photoScanning && intervals.current.length > 0) {
      clearIntervals();
    }
  }, [addInterval, article, clearIntervals, intervals, refetch]);

  const sanitizedText = useSanitizeHtml({ journeyId, rawHtml: article.text, resource: article });
  const sanitizedTranscript = useSanitizeHtml({ journeyId, rawHtml: article.transcript, resource: article });

  useEffect(() => {
    if (sessionStorage.getItem(automaticallyRegisterForArticleIdKey) === article.id) {
      sessionStorage.removeItem(automaticallyRegisterForArticleIdKey);
      sessionStorage.removeItem(automaticallyRegisterForArticleStartTimeKey);
    }
  }, [article.id]);

  useEffect(() => {
    // What we want here is to create the analytics event _only if_ the client created their account specifically
    // in order to attend the webinar. This is indicated by their account createdAt time being later than the time
    // when they originally clicked on the registration button. That time is passed in the param, so we can compare
    // them. Previously, the time when they clicked on the button wasn't stored in this way and the param was passed
    // as true, so we need to continue to handle that case. If some other weird case happens where the time is not
    // parseable then just don't create the event.
    if (myClient && !article.webinarRegistration && registerAutomaticallyIdParam === article.id) {
      if (registerAutomaticallyStartTimeParam === "true") {
        createRegistrations({
          addAnalyticsEvent: true,
          article,
          journeyId,
          recurrenceType: WebinarRegistrationRecurrenceTypeEnum.SINGLE_OCCURRENCE
        });
      } else if (registerAutomaticallyStartTimeParam) {
        try {
          const timestamp = new Date(parseInt(registerAutomaticallyStartTimeParam));
          createRegistrations({
            addAnalyticsEvent: timestamp < myClient.createdAt,
            article,
            journeyId,
            recurrenceType: WebinarRegistrationRecurrenceTypeEnum.SINGLE_OCCURRENCE
          });
        } catch {
          createRegistrations({
            addAnalyticsEvent: false,
            article,
            journeyId,
            recurrenceType: WebinarRegistrationRecurrenceTypeEnum.SINGLE_OCCURRENCE
          });
        }
      }
    }
  }, [
    article,
    createRegistrations,
    journeyId,
    myClient,
    registerAutomaticallyIdParam,
    registerAutomaticallyStartTimeParam
  ]);

  return (
    <div className={classnames("component-Article", styles.articleLayout)}>
      <div className={styles.columnLeft}>
        {myUser && (
          <div className={styles.backButton}>
            <DirectionButton
              direction="back"
              onClick={() => {
                history.goBack();
              }}
            />
          </div>
        )}
      </div>

      <div className={styles.columnCenter}>
        <div className={styles.articleContainer}>
          {isSmallOrSmallerScreen ? (
            <ArticleIntroMobile article={article} journeyId={journeyId} />
          ) : (
            <ArticleIntro article={article} journeyId={journeyId} />
          )}
          <div className={styles.body}>
            {article.contentType === ArticleContentTypeEnum.WEBINAR &&
              !article.webinarCompleted &&
              article.webinarRegistration?.zoomJoinUrl &&
              webinarIsToday && (
                <div className={styles.joinSpacing}>
                  <Button href={article.webinarRegistration.zoomJoinUrl} rel="noopener noreferrer" target="_blank">
                    <IconBase icon="video" slot="start" />
                    <FormattedMessage id="models.consult.join" />
                  </Button>
                </div>
              )}

            {[ArticleContentTypeEnum.TEXT, ArticleContentTypeEnum.WEBINAR].includes(article.contentType) && (
              <div>{sanitizedText}</div>
            )}

            {articleHasVideo(article) && (
              <BlockAccordion
                border={false}
                color="white"
                content={<div className={styles.accordionContent}>{sanitizedTranscript}</div>}
                heading={<FormattedMessage id="dictionary.viewTranscript" />}
              />
            )}
            {article.sources.length > 0 && (
              <BlockAccordion
                border={false}
                color="white"
                content={
                  <div className={styles.accordionContent}>
                    <div className={styles.articleSources}>
                      {article.sources.map(source => (
                        <div key={source.value}>
                          <ExternalLink href={source.value} linkEventCreateVariables={{ journeyId }}>
                            {source.label}
                          </ExternalLink>
                        </div>
                      ))}
                    </div>
                  </div>
                }
                heading={<FormattedMessage id="models.article.sources" />}
              />
            )}
          </div>
        </div>

        {journeyId && article.relatedArticles.length > 0 && (
          <div className={styles.relatedContent}>
            <div className={styles.relatedContentHeading}>
              <Text color="primary" tag="h2" variant="lg">
                <FormattedMessage id="pages.article.relatedContent" />
              </Text>
            </div>

            <CardSlides itemCount={article.relatedArticles.length}>
              {article.relatedArticles.map(article => (
                <SwiperSlide key={article.id}>
                  <ArticleCard article={article} journeyId={journeyId} />
                </SwiperSlide>
              ))}
            </CardSlides>
          </div>
        )}
      </div>

      {!isMediumOrSmallerScreen && (
        <div className={styles.columnRight}>
          <div className={styles.quote}>
            <Text color="gray-300" variant="2xlSerif">
              <FormattedMessage id="dictionary.punctuation.quoteLeft" />
              {article.quote}
              <FormattedMessage id="dictionary.punctuation.quoteRight" />
            </Text>
          </div>
        </div>
      )}
    </div>
  );
};

export default Article;
